import {
    Card,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    SearchInput,
    Switch,
    ActionButton,
} from "@jhool-io/fe-components";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import { startOfWeek, endOfWeek, format, addWeeks, subWeeks } from "date-fns";
import { useAppSelector } from "../../../../../hooks/useRedux";
import {
    displayNameInRightFormat,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../../../../utils/helpers";
import CaseloadList from "../CaseloadList/CaseloadList";
import SuperviseeCaseloadList from "../SuperviseeCaseloadList/SuperviseeCaseloadList";
import MyWeekView from "../MyWeekView/MyWeekView";
import { useFetchClientList } from "../../../../../hooks/queries/client";
import TableFiltersDisplay from "../../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import ChevronLeftIcon from "../../../../../icons/ChevronLeft";
import ChevronRightIcon from "../../../../../icons/ChevronRight";

export default function CaseloadAndCalendarWrapper() {
    const [currentWeek, setCurrentWeek] = useState(() =>
        startOfWeek(new Date(), { weekStartsOn: 1 })
    );
    const [searchParams, setSearchParams] = useSearchParams();

    const { practice } = useAppSelector((state) => state.userPractice);

    const tabInUrl = searchParams.get("view") || "week";

    const filter = searchParams.get("filter");
    const showPausedFilter = searchParams.get("show_paused");
    const clientFilter = searchParams.getAll("client_ids[]") || [];
    const searchFilter = searchParams.get("search") || "";

    const showPausedClientFilterTextToShow = () => {
        if (showPausedFilter === "true") {
            return "Paused clients";
        }

        if (showPausedFilter === "false") {
            return "Unpaused clients";
        }

        return "Show all appointments";
    };

    const startOfCurrentWeek = startOfWeek(currentWeek, { weekStartsOn: 1 });
    const endOfCurrentWeek = endOfWeek(currentWeek, { weekStartsOn: 1 });

    const startOfWeekFormatted = format(startOfCurrentWeek, "MMM d");
    const endOfWeekFormatted = format(endOfCurrentWeek, "MMM d, yyyy");

    const currentWeekRange = `${startOfWeekFormatted} - ${endOfWeekFormatted}`;

    const { data: clientData } = useFetchClientList({
        page: 1,
        provider_id: practice?.provider_id as string,
    });

    const filteredClientNames = (clientData?.data ?? [])
        .filter((client) => clientFilter.includes(client.client_id))
        .map((client) =>
            displayNameInRightFormat({
                firstName: client.first_name,
                lastName: client.last_name,
            })
        );

    const handleTabSwitch = (tab: string) => {
        searchParams.set("view", tab);

        setSearchParams(`view=${tab}`);
    };

    const switchOptions = [
        {
            key: "week",
            title: "Week",
            onClick: () => {
                handleTabSwitch("week");
                searchParams.set(
                    "from_date",
                    format(startOfCurrentWeek, "yyyy-MM-dd")
                );
                searchParams.set(
                    "to_date",
                    format(endOfCurrentWeek, "yyyy-MM-dd")
                );
                setSearchParams(searchParams);
            },
        },
        {
            key: "my_caseload",
            title: "My Caseload",
            onClick: () => handleTabSwitch("my_caseload"),
        },
        {
            key: "supervisee_caseload",
            title: "Supervisee's CL",
            onClick: () => handleTabSwitch("supervisee_caseload"),
            hidden: !practice?.is_supervisor,
        },
    ];

    const updateWeekRange = (direction: string) => {
        const newWeek =
            direction === "next"
                ? addWeeks(currentWeek, 1)
                : subWeeks(currentWeek, 1);

        const weekStart = startOfWeek(newWeek, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(newWeek, { weekStartsOn: 1 });

        searchParams.set("from_date", format(weekStart, "yyyy-MM-dd"));
        searchParams.set("to_date", format(weekEnd, "yyyy-MM-dd"));
        setSearchParams(searchParams);
        setCurrentWeek(newWeek);
    };

    const handleSetFilter = (filterType: string) => {
        if (filterType === "") searchParams.delete("filter");
        else searchParams.set("filter", filterType);

        setSearchParams(searchParams);
    };
    const handleSetPausedClientsFilter = (showPaused: string) => {
        if (showPaused === "") searchParams.delete("show_paused");
        else searchParams.set("show_paused", showPaused);

        setSearchParams(searchParams);
    };

    const handleClientFilter = (client_id: string) => {
        if (clientFilter.includes(client_id)) {
            searchParams.delete("client_ids[]", client_id);
        } else {
            searchParams.append("client_ids[]", client_id);
        }
        setSearchParams(searchParams);
    };

    const handleSearchFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search");
        else searchParams.set("search", e.target.value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    return (
        <Card classNames="p-0">
            <div className="flex items-center justify-between gap-[--spacing-items] py-8 flex-wrap [border-bottom:1px_solid_var(--color-stroke)] md:overflow-x-hidden px-[--padding-card-x-lg]">
                <div>
                    {tabInUrl === "week" ? (
                        <>
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>Client</FilterButton>
                                </DropdownTrigger>
                                <DropdownContent width="auto" maxHeight={216}>
                                    {clientData?.data?.map((client) => (
                                        <DropdownItem
                                            key={client.client_id}
                                            onClick={() =>
                                                handleClientFilter(
                                                    client.client_id
                                                )
                                            }
                                            isMulti
                                            itemValue={client.client_id}
                                            values={clientFilter}
                                            closeMenuOnSelect
                                        >
                                            {displayNameInRightFormat({
                                                firstName: client.first_name,
                                                lastName: client.last_name,
                                            })}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>
                            <TableFiltersDisplay
                                appliedFilters={[
                                    {
                                        key: "client_ids[]",
                                        values: filteredClientNames as string[],
                                    },
                                ]}
                                wrapperClass="mt-12"
                            />
                        </>
                    ) : (
                        <SearchInput
                            placeholder="Search by client name"
                            containerClass="w-[250px]"
                            onChange={handleSearchFilter}
                            defaultValue={searchFilter}
                        />
                    )}
                </div>
                <div className="flex items-center gap-[--spacing-items] flex-wrap">
                    {tabInUrl === "my_caseload" ? (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton>
                                    {filter
                                        ? makeStringFirstLetterCapital(
                                              truncateString(
                                                  removeEnumUnderscore(filter)
                                              )
                                          )
                                        : "Show All Clients"}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent width="auto" align="end">
                                <DropdownItem
                                    onClick={() => handleSetFilter("")}
                                    isSelected={!filter}
                                >
                                    All clients
                                </DropdownItem>
                                <DropdownItem
                                    onClick={() =>
                                        handleSetFilter(
                                            "clients_with_pending_task"
                                        )
                                    }
                                    isSelected={
                                        filter === "clients_with_pending_task"
                                    }
                                >
                                    Clients with pending tasks
                                </DropdownItem>
                            </DropdownContent>
                        </Dropdown>
                    ) : null}
                    {tabInUrl === "my_caseload" ? (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton>
                                    {showPausedClientFilterTextToShow()}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent align="end" width={180}>
                                <DropdownItem
                                    onClick={() =>
                                        handleSetPausedClientsFilter("")
                                    }
                                    isSelected={!showPausedFilter}
                                >
                                    All clients
                                </DropdownItem>
                                <DropdownItem
                                    onClick={() =>
                                        handleSetPausedClientsFilter("false")
                                    }
                                    isSelected={showPausedFilter === "false"}
                                >
                                    Unpaused clients
                                </DropdownItem>
                                <DropdownItem
                                    onClick={() =>
                                        handleSetPausedClientsFilter("true")
                                    }
                                    isSelected={showPausedFilter === "true"}
                                >
                                    Paused clients
                                </DropdownItem>
                            </DropdownContent>
                        </Dropdown>
                    ) : null}

                    {tabInUrl === "week" ? (
                        <div className="flex items-center gap-x-[--spacing-items] text-base font-medium">
                            <ActionButton
                                variant="can-hover"
                                size="auto"
                                onClick={() => updateWeekRange("previous")}
                                className="rounded-full [border:1px_solid_var(--color-stroke)] size-7 p-0"
                                actionTitle="Previous week"
                            >
                                <ChevronLeftIcon className="stroke-text" />
                            </ActionButton>
                            <span>{currentWeekRange}</span>
                            <ActionButton
                                variant="can-hover"
                                size="auto"
                                className="rounded-full [border:1px_solid_var(--color-stroke)] size-7 p-0"
                                actionTitle="Next week"
                                onClick={() => updateWeekRange("next")}
                            >
                                <ChevronRightIcon className="stroke-text" />
                            </ActionButton>
                        </div>
                    ) : null}

                    <Switch
                        switchOptions={switchOptions}
                        activeSwitch={tabInUrl || "week"}
                    />
                </div>
            </div>
            <div className="p-16">
                {tabInUrl === "week" ? <MyWeekView /> : null}
                {tabInUrl === "my_caseload" ? <CaseloadList /> : null}
                {tabInUrl === "supervisee_caseload" ? (
                    <SuperviseeCaseloadList />
                ) : null}
            </div>
        </Card>
    );
}
